import { PatchDossierDetails, Reikwijdte, Traject, usePatchDossierDetailsById } from "@/entities/dossier-details";
import {
    Button,
    CheckboxFormField,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    Form,
} from "@/shared/components";
import { RadixBaseUiInteropContextProvider } from "@/shared/components/utils/radix-base-ui-interop-provider.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, SetStateAction, useRef } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { usePresetComparison } from "../../hooks";
import { getActieAndPayload, updateDetailsSchema, UpdateDossierDetailsData } from "../../utils";
import { Omschrijvingen, ReikwijdtePicker } from "../shared";
import { SectionsTooltip } from "./sections-tooltip";

type EditDetailsDialogProps = {
    dossierId: string;
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
    omschrijving: string;
    omschrijvingExtraContext: string | null;
    reikwijdte: Reikwijdte;
    trajecten: Traject[];
    stations: string[];
};

export function EditDetailsDialog({
    dossierId,
    open,
    onOpenChange,
    omschrijving,
    reikwijdte,
    trajecten,
    stations,
    omschrijvingExtraContext,
}: EditDetailsDialogProps) {
    const radixRef = useRef<HTMLDivElement | null>(null);
    const patchDetails = usePatchDossierDetailsById();

    const form = useForm<UpdateDossierDetailsData>({
        resolver: zodResolver(updateDetailsSchema),
        defaultValues: {
            omschrijving: omschrijving,
            omschrijvingExtraContext: omschrijvingExtraContext ?? "",
            reikwijdte: reikwijdte,
            trajecten,
            stations,
            updateSections: false,
        },
    });

    const { sectionsDiffer, nextSections } = usePresetComparison(dossierId, form, omschrijving, reikwijdte);

    // Destructure formState here to ensure updates, see https://github.com/react-hook-form/react-hook-form/issues/1146
    const { dirtyFields } = form.formState;

    const handleSubmit: SubmitHandler<UpdateDossierDetailsData> = (data) => {
        const { actie, payload } = getActieAndPayload(
            dirtyFields,
            { ...data, updateSections: sectionsDiffer && data.updateSections },
            reikwijdte
        );

        const unchanged = Object.keys(payload).length === 1; // this means updateSections is false and there are no other changes, because the only way to set updateSections to true is when there are other changes. No need to make the PATCH call
        if (unchanged) {
            onOpenChange(false);
            return;
        }

        const patchBody: Omit<PatchDossierDetails, "dossierId"> = {
            actie,
            reikwijdte,
            ...payload,
        };

        patchDetails.mutate({ dossierId, ...patchBody }, { onSuccess: () => onOpenChange(false) });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <RadixBaseUiInteropContextProvider radixElement={radixRef}>
                <DialogContainer ref={radixRef}>
                    <DialogHeader>
                        <DialogTitle>Wijzig dossier details</DialogTitle>
                        <DialogDescription className="sr-only">
                            Wijzig de omschrijving, extra context en reikwijdte voor dit dossier
                        </DialogDescription>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}>
                            <DialogBody className="grid gap-4">
                                <Omschrijvingen />
                                <ReikwijdtePicker />
                                {sectionsDiffer && (
                                    <div className="space-y-2">
                                        <p className="text-content-subtle text-s leading-m" data-testid="secties-helptext">
                                            De secties die standaard bij de nieuwe omschrijving en reikwijdte horen, zijn anders dan bij de
                                            huidige omschrijving en reikwijdte. Wil je de huidige selectie van secties behouden, of wil je
                                            deze aanpassen aan de nieuwe omschrijving en reikwijdte? Er gaat geen data verloren.
                                        </p>

                                        <div className="flex items-center gap-2">
                                            <CheckboxFormField
                                                form={form}
                                                name="updateSections"
                                                label="Secties aanpassen aan nieuwe omschrijving en reikwijdte"
                                            />
                                            {nextSections && <SectionsTooltip settings={nextSections} />}
                                        </div>
                                    </div>
                                )}
                                {patchDetails.error && (
                                    <ErrorDisplay error={patchDetails.error} title="Fout bij het wijzigen van details" />
                                )}
                            </DialogBody>
                            <DialogFooter>
                                <Button type="button" variant="tertiary" onClick={() => onOpenChange(false)}>
                                    Annuleren
                                </Button>
                                <Button type="submit" variant="primary" disabled={patchDetails.isPending}>
                                    Opslaan
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContainer>
            </RadixBaseUiInteropContextProvider>
        </Dialog>
    );
}
